import { supabase } from './supabase/client';

/**
 * Checks if a user profile exists in the database
 * 
 * @param userId - The user ID to check
 * @returns Boolean indicating if profile exists
 */
export async function checkUserProfileExists(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();
      
    if (error && error.code !== 'PGRST116') {
      console.error('Error checking profile existence:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Unexpected error checking profile:', error);
    return false;
  }
}

/**
 * Creates a user profile using the create_user_profile RPC function
 * 
 * @param userId - The user ID 
 * @param email - The user's email
 * @param fullName - The user's display name
 * @returns Boolean indicating success
 */
export async function createUserProfile(
  userId: string,
  email: string,
  fullName: string
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('create_user_profile', {
      user_id_param: userId,
      email_param: email,
      full_name_param: fullName
    });
    
    if (error) {
      console.error('Error creating user profile:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Unexpected error creating user profile:', error);
    return false;
  }
}

/**
 * Ensures a user has the necessary profile records
 * 
 * @param userId - The user ID
 * @param email - The user's email
 * @param fullName - Optional display name, defaults to email username
 * @returns Boolean indicating success
 */
export async function ensureUserProfile(
  userId: string,
  email: string,
  fullName?: string
): Promise<boolean> {
  try {
    // Use email username as display name if not provided
    const displayName = fullName || email.split('@')[0];
    
    // Check if profile exists
    const profileExists = await checkUserProfileExists(userId);
    
    // If profile doesn't exist, create it
    if (!profileExists) {
      console.log(`Creating profile for user ${userId}`);
      return await createUserProfile(userId, email, displayName);
    }
    
    // Profile already exists
    return true;
  } catch (error) {
    console.error('Error ensuring user profile:', error);
    return false;
  }
}

/**
 * Creates a default cash account for a user
 * 
 * @param userId - The user ID
 * @returns Boolean indicating success
 */
export async function createDefaultAccount(userId: string): Promise<boolean> {
  try {
    // Check if user already has accounts
    const { data: accountData, error: accountError } = await supabase
      .from('accounts')
      .select('id')
      .eq('user_id', userId)
      .limit(1);
      
    if (accountError) {
      console.error('Error checking existing accounts:', accountError);
      return false;
    }
    
    // Only create account if user doesn't have any
    if (!accountData || accountData.length === 0) {
      const { error: createError } = await supabase
        .from('accounts')
        .insert({
          user_id: userId,
          name: 'Cash',
          type: 'cash',
          currency: 'USD',
          balance: 0,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
      if (createError) {
        console.error('Error creating default account:', createError);
        return false;
      }
      
      console.log(`Created default cash account for user ${userId}`);
      return true;
    }
    
    // User already has accounts
    return true;
  } catch (error) {
    console.error('Unexpected error creating default account:', error);
    return false;
  }
}

export const profileService = {
  checkUserProfileExists,
  createUserProfile,
  ensureUserProfile,
  createDefaultAccount
}; 